import React from 'react';
import { block } from '../utils/cn';
import { useForkRef } from '../utils/useForkRef';
import { TextAreaControl } from './TextAreaControl/TextAreaControl';
import { InputControl } from './InputControl/InputControl';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { CrossIcon } from '../icons/CrossIcon';
import i18n from './i18n';
import './TextInput.css';
const b = block('text-input');
const getTextInputState = (args = {}) => {
    const { error } = args;
    return error ? 'error' : undefined;
};
const prepareAutoComplete = (autoComplete) => {
    if (typeof autoComplete === 'boolean') {
        return autoComplete ? 'on' : 'off';
    }
    else {
        return autoComplete;
    }
};
export const TextInput = React.forwardRef(function TextInput(props, ref) {
    const { view = 'normal', size = 'm', pin = 'round-round', name, value, defaultValue, disabled = false, multiline = false, hasClear = false, error, autoComplete, onUpdate, onChange, id, tabIndex, style, className, qa, } = props;
    const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue !== null && defaultValue !== void 0 ? defaultValue : '');
    const innerControlRef = React.useRef(null);
    const [hasVerticalScrollbar, setHasVerticalScrollbar] = React.useState(false);
    const isControlled = value !== undefined;
    const inputValue = isControlled ? value : uncontrolledValue;
    const handleRef = useForkRef(props.controlRef, innerControlRef);
    React.useEffect(() => {
        const control = innerControlRef.current;
        if (control && multiline) {
            const currHasVerticalScrollbar = control.scrollHeight > control.clientHeight;
            if (hasVerticalScrollbar !== currHasVerticalScrollbar) {
                setHasVerticalScrollbar(currHasVerticalScrollbar);
            }
        }
    }, [multiline, inputValue, hasVerticalScrollbar]);
    const state = React.useMemo(() => getTextInputState({ error }), [error]);
    const handleClear = (event) => {
        const control = innerControlRef.current;
        if (control) {
            control.focus();
            const syntheticEvent = Object.create(event);
            syntheticEvent.target = control;
            syntheticEvent.currentTarget = control;
            control.value = '';
            if (onChange) {
                onChange(syntheticEvent);
            }
            if (onUpdate) {
                onUpdate('');
            }
        }
        if (!isControlled) {
            setUncontrolledValue('');
        }
    };
    const isErrorMsgVisible = typeof error === 'string';
    const isClearControlVisible = Boolean(hasClear && !disabled && inputValue);
    const commonProps = {
        id,
        tabIndex,
        name,
        onChange(event) {
            const newValue = event.target.value;
            if (!isControlled) {
                setUncontrolledValue(newValue);
            }
            if (onChange) {
                onChange(event);
            }
            if (onUpdate) {
                onUpdate(newValue);
            }
        },
        autoComplete: prepareAutoComplete(autoComplete),
    };
    return (React.createElement("span", { ref: ref, style: style, className: b({
            view,
            size,
            pin: view === 'clear' ? undefined : pin,
            disabled,
            state,
            'has-clear': hasClear,
            'has-scrollbar': hasVerticalScrollbar,
        }, className), "data-qa": qa },
        props.multiline ? (React.createElement(TextAreaControl, Object.assign({}, props, commonProps, { controlRef: handleRef }))) : (React.createElement(InputControl, Object.assign({}, props, commonProps, { controlRef: handleRef }))),
        isErrorMsgVisible && React.createElement("div", { className: b('error') }, error),
        hasClear && (React.createElement(Button, { size: size, className: b('clear', { visible: isClearControlVisible }), onClick: handleClear, extraProps: { 'aria-label': i18n('label_clear-button') } },
            React.createElement(Icon, { data: CrossIcon, size: 10 })))));
});
